import { Link } from 'react-router-dom';
import Card from '../../../components/base/Card';
import useScrollReveal from '@/hooks/useScrollReveal';
import { useLanguage } from '@/localization';
import {
  type NewsItem,
  formatDate,
  itemDate,
  newsTagLabel,
  parseNewsList,
  sortPinnedThenNewest,
} from '../../news/shared';

type FeedState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; items: NewsItem[] };

/**
 * NewsSection — KF1-tagged community news teaser on the homepage. Fetches
 * `GET /api/news?tag=kf1` (Vite dev proxy forwards `/api` → the platform API on
 * :8078) and renders a featured slot (pinned posts; carousel when more than one
 * is pinned, most-recent post when none are) plus the latest 3 cards and a
 * "View all news →" link to the dedicated `/news` page. Each card links to its
 * `/news/:id` detail page — no inline expand/collapse.
 *
 * @returns {JSX.Element} The news section component
 */
export default function NewsSection() {
  const sectionRef = useScrollReveal();
  const { apiPath, t } = useLanguage();
  const [state, setState] = useState<FeedState>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });

    async function load() {
      try {
        const res = await fetch(apiPath('/api/news?tag=kf1'), { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: unknown = await res.json();
        setState({ status: 'ready', items: parseNewsList(data) });
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setState({ status: 'error' });
      }
    }

    void load();
    return () => controller.abort();
  }, [apiPath]);

  return (
    <section id="news" ref={sectionRef} className="relative z-10 scroll-mt-20 py-20 bg-black/80">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16 scroll-reveal">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-orbitron horror-heading">
            <span className="text-red-500">{t('Latest News')}</span>
          </h2>
          <div className="w-24 h-1 bg-red-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('Server updates, new perks, events, and announcements from the Killing Floor front.')}
          </p>
        </div>

        {state.status === 'loading' && <NewsSkeleton />}
        {state.status === 'error' && <NewsError />}
        {state.status === 'ready' &&
          (state.items.length === 0 ? <NewsEmpty /> : <NewsTeaser items={state.items} />)}
      </div>
    </section>
  );
}

/**
 * Splits the feed into a featured slot (pinned posts, or the most recent post
 * if none are pinned) and the next 3 most-recent posts not already featured.
 */
function NewsTeaser({ items }: { items: NewsItem[] }) {
  const { t } = useLanguage();
  const sorted = useMemo(() => sortPinnedThenNewest(items), [items]);
  const pinned = useMemo(() => sorted.filter((i) => i.pinned), [sorted]);

  const featured = pinned.length > 0 ? pinned : sorted.slice(0, 1);
  const featuredIds = new Set(featured.map((i) => i.id));
  const latest = sorted.filter((i) => !featuredIds.has(i.id)).slice(0, 3);

  return (
    <div className="max-w-6xl mx-auto">
      {featured.length > 1 ? (
        <FeaturedCarousel items={featured} />
      ) : (
        featured[0] && <FeaturedCard item={featured[0]} />
      )}

      {latest.length > 0 && (
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {latest.map((item) => (
            <NewsCard key={item.id} item={item} />
          ))}
        </div>
      )}

      <div className="mt-12 text-center">
        <Link
          to="/news"
          className="inline-flex items-center gap-2 rounded-lg border border-red-800/60 bg-red-950/30 px-6 py-3 font-bold text-red-300 transition-colors hover:bg-red-900/40 hover:text-red-200 font-orbitron"
        >
          {t('View all news')}
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    </div>
  );
}

/** Hand-rolled carousel for multiple pinned/featured posts (prev/next + dots). */
function FeaturedCarousel({ items }: { items: NewsItem[] }) {
  const { t } = useLanguage();
  const [index, setIndex] = useState(0);
  const count = items.length;
  const go = (next: number) => setIndex(((next % count) + count) % count);

  return (
    <div className="relative">
      <FeaturedCard item={items[index]} />

      <div className="mt-5 flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label={t('Previous featured post')}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-red-800/60 bg-black/60 text-red-300 transition-colors hover:bg-red-900/40 hover:text-red-200"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>

        <div className="flex items-center gap-2">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => go(i)}
              aria-label={`Go to featured post ${i + 1}`}
              aria-current={i === index}
              className={`h-2.5 rounded-full transition-all ${
                i === index ? 'w-6 bg-red-500' : 'w-2.5 bg-red-900/60 hover:bg-red-700/70'
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label={t('Next featured post')}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-red-800/60 bg-black/60 text-red-300 transition-colors hover:bg-red-900/40 hover:text-red-200"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>
    </div>
  );
}

/** Large featured card linking to the post's detail page. */
function FeaturedCard({ item }: { item: NewsItem }) {
  const { t } = useLanguage();
  const date = formatDate(itemDate(item));
  return (
    <Card variant="blood" className="relative overflow-hidden">
      <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full border border-red-700/60 bg-red-950/60 px-3 py-1 text-xs font-bold uppercase tracking-wider text-red-300">
        <i className="ri-star-fill text-[10px]"></i>
        {t('Featured')}
      </span>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {item.tag && (
          <span className="inline-flex items-center rounded-full border border-red-900/40 bg-red-900/30 px-3 py-1 text-xs font-bold uppercase tracking-wider text-red-400">
            {t(newsTagLabel(item.tag))}
          </span>
        )}
        {date && (
          <time dateTime={itemDate(item)} className="text-xs font-medium text-gray-400">
            {date}
          </time>
        )}
      </div>
      <h3 className="mb-3 max-w-3xl font-orbitron text-2xl font-bold leading-snug text-white md:text-3xl">
        {item.title}
      </h3>
      {item.excerpt && (
        <p className="max-w-3xl leading-relaxed text-gray-300">{item.excerpt}</p>
      )}
      <Link
        to={`/news/${item.id}`}
        className="group mt-5 inline-flex items-center gap-1 text-sm font-semibold text-red-400 transition-colors hover:text-red-300"
      >
        {t('Read full update')}
        <i className="ri-arrow-right-s-line transition-transform group-hover:translate-x-0.5"></i>
      </Link>
    </Card>
  );
}

/** Compact card for the latest-news grid; links to the detail page. */
function NewsCard({ item }: { item: NewsItem }) {
  const { t } = useLanguage();
  const date = formatDate(itemDate(item));
  return (
    <Link to={`/news/${item.id}`} className="group block h-full">
      <Card
        variant="dark"
        className="flex h-full flex-col transition-transform duration-300 group-hover:scale-[1.02] group-hover:border-red-800/60"
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          {item.tag && (
            <span className="inline-flex items-center rounded-full border border-red-900/40 bg-red-900/30 px-3 py-1 text-xs font-bold uppercase tracking-wider text-red-400">
              {t(newsTagLabel(item.tag))}
            </span>
          )}
          {date && (
            <time dateTime={itemDate(item)} className="text-xs font-medium text-gray-400">
              {date}
            </time>
          )}
        </div>

        <h3 className="mb-3 font-orbitron text-xl font-bold leading-snug text-white">
          {item.title}
        </h3>

        {item.excerpt && (
          <p className="flex-1 leading-relaxed text-gray-300">{item.excerpt}</p>
        )}

        <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-red-400 transition-colors group-hover:text-red-300">
          {t('Read more')}
          <i className="ri-arrow-right-s-line transition-transform group-hover:translate-x-0.5"></i>
        </span>
      </Card>
    </Link>
  );
}

function NewsSkeleton() {
  return (
    <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i} variant="dark" className="animate-pulse">
          <div className="mb-4 flex items-center justify-between">
            <div className="h-5 w-16 rounded-full bg-gray-800"></div>
            <div className="h-3 w-20 rounded bg-gray-800"></div>
          </div>
          <div className="mb-3 h-5 w-3/4 rounded bg-gray-800"></div>
          <div className="space-y-2">
            <div className="h-3 w-full rounded bg-gray-800"></div>
            <div className="h-3 w-5/6 rounded bg-gray-800"></div>
            <div className="h-3 w-2/3 rounded bg-gray-800"></div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function NewsEmpty() {
  const { t } = useLanguage();
  return (
    <Card variant="dark" className="mx-auto max-w-2xl border-dashed text-center">
      <i className="ri-newspaper-line mb-3 block text-4xl text-gray-500"></i>
      <p className="font-orbitron text-lg font-semibold text-gray-200">{t('No news yet')}</p>
      <p className="mt-1 text-sm text-gray-400">
        {t('Check back soon for server updates and announcements.')}
      </p>
    </Card>
  );
}

function NewsError() {
  const { t } = useLanguage();
  return (
    <Card variant="blood" className="mx-auto max-w-2xl text-center">
      <i className="ri-error-warning-line mb-3 block text-4xl text-red-400"></i>
      <p className="font-orbitron text-lg font-semibold text-white">{t("Couldn't load the news feed")}</p>
      <p className="mt-1 text-sm text-gray-300">{t('Please try again in a little while.')}</p>
    </Card>
  );
}
